import { ref, watch } from 'vue';
import date from 'quasar/src/utils/date/date.js';
const { getDateDiff } = date;
import { dtWithLocaleFormat } from 'src/plugins/date/dtWithLocale';
import { from } from 'rxjs';
import { filter, map, scan, switchMap, tap } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { ChatMiscDataPlugin } from 'src/features/messaging/chat/components/methods/misc';
import { redactSensitiveInfo } from 'src/features/messaging/methods/redactMsg';
/**
 * Plugin containing misc methods for Chat component
 */
const ChatProcessMsgPlugin = () => {
    const { convo, liveConvo } = ChatMiscDataPlugin();
    const { formatDateWithSysLocale } = dtWithLocaleFormat();
    const processedMsg = ref([]);
    const { subject: liveConvoWatcher$ } = rxInitSubjectCallback();
    useObservable(liveConvoWatcher$.pipe(map((data) => data[convo.value.id]), filter((data) => data !== undefined), map((convoData) => convoData.messages), switchMap((messages) => {
        const msgProcessed = [];
        let dateSet = {
            date: '',
            messages: []
        };
        let textSet = [];
        let previousMsg = null;
        return from(messages).pipe(map((msg) => {
            /**
             * Parse Date Data to msg
             */
            const processDate = msg.createdAt
                ? msg.createdAt.toDate()
                : new Date();
            const nowDate = new Date();
            const dateTxt = formatDateWithSysLocale(processDate, 'DD MMMM YYYY');
            const mins = getDateDiff(nowDate, processDate, 'minutes');
            const processTxt = formatDateWithSysLocale(processDate, 'HH:mm');
            if (!dateSet.date) {
                dateSet.date = dateTxt;
            }
            return {
                msg: {
                    ...msg,
                    time_txt: processTxt,
                    mins: mins
                },
                dateTxt
            };
        }), tap(({ msg, dateTxt }) => {
            // push to set if the date is different
            if ((dateSet.messages.length > 0) &&
                (dateTxt !== dateSet.date)) {
                msgProcessed.push(dateSet);
                dateSet = {
                    'date': dateTxt,
                    'messages': []
                };
            }
            // remove sensitive info if needed
            const msgTxt = msg.run_redaction_logic === false
                ? msg.text
                : redactSensitiveInfo(msg.text);
            if (((previousMsg !== null) && (msg.sender !== previousMsg.sender)) || (msg.type !== 'text')) {
                // if previous message was text type,
                // the text will accumulate
                // so we push in previous message
                if (previousMsg !== null && previousMsg.type === 'text') {
                    const lastMsg = textSet.pop() || '';
                    dateSet.messages.push({
                        ...previousMsg,
                        text: textSet,
                        lastMsg: lastMsg
                    });
                    textSet = [];
                }
                if (msg.type !== 'text') {
                    // push in the non text type of data
                    dateSet.messages.push({
                        ...msg,
                        text: [],
                        lastMsg: msgTxt
                    });
                }
            }
            if (msg.type === 'text') {
                textSet.push(msgTxt);
            }
            previousMsg = msg;
        }), scan((total) => total += 1, 0), filter((total) => total === messages.length), tap(() => {
            // push in the last text type of data
            if (previousMsg !== null && previousMsg.type === 'text') {
                const lastMsg = textSet.pop() || '';
                dateSet.messages.push({
                    ...previousMsg,
                    text: textSet,
                    lastMsg: lastMsg
                });
                textSet = [];
            }
            if (dateSet.messages.length > 0) {
                msgProcessed.push(dateSet);
            }
        }), tap(() => {
            processedMsg.value = msgProcessed;
        }));
    })));
    watch(liveConvo, (newValue) => {
        liveConvoWatcher$.next(newValue);
    }, { immediate: true });
    return {
        processedMsg
    };
};
export { ChatProcessMsgPlugin };
